import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retryWhen, take } from 'rxjs/operators';
import { GtmService } from './gtm.service';

interface TypeformHashedData {
  form_id: string;
  submitted_at: string;
  hashed_email?: string;
  hashed_phone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TypeformService {
  private apiEndpoint = '/api/typeform-data';
  private maxRetries = 5;
  private retryDelay = 2000; // 2 seconds

  constructor(
    private http: HttpClient,
    private gtmService: GtmService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Retrieves hashed form data from the server using the token
   * @param token The Typeform response token
   * @param sessionId Optional session ID for additional validation
   */
  getHashedFormData(
    token: string,
    sessionId?: string
  ): Observable<TypeformHashedData> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(
        () => new Error('This method must be called from the browser')
      );
    }

    const params: any = { token };
    if (sessionId) {
      params.sessionId = sessionId;
    }

    return this.http.get<TypeformHashedData>(this.apiEndpoint, { params }).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, index) => {
            if (index >= this.maxRetries || error.status !== 404) {
              return throwError(() => error);
            }
            // Retry with exponential backoff for 404 (data might not be ready yet)
            const delay = this.retryDelay * Math.pow(2, index);
            console.log(`Retrying Typeform data fetch in ${delay}ms...`);
            return timer(delay);
          }),
          take(this.maxRetries + 1)
        )
      ),
      catchError((error) => {
        console.error('Error fetching Typeform data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Handles Typeform submission completion
   * @param formId The Typeform ID
   * @param responseToken The response token from Typeform
   */
  async handleFormSubmission(
    formId: string,
    responseToken: string
  ): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Session ID is optional and not currently used in validation
      // const sessionId = this.getSessionId();

      // Fetch pre-hashed data from server (data was hashed when stored in Redis)
      const hashedData = await this.getHashedFormData(
        responseToken
      ).toPromise();

      if (!hashedData) {
        throw new Error('No data received from server');
      }

      // Push to GTM data layer
      this.pushToDataLayer(formId, hashedData);

      // Also push to Meta and TikTok if needed
      this.pushToMetaPixel(hashedData);
      this.pushToTikTokPixel(hashedData);
    } catch (error) {
      console.error('Error handling Typeform submission:', error);
      // Push error event to data layer
      this.gtmService.pushToDataLayer({
        event: 'typeform_error',
        form_id: formId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  /**
   * Push hashed data to GTM data layer
   */
  private pushToDataLayer(formId: string, data: TypeformHashedData): void {
    const payload = {
      event: 'typeform_submission',
      form_id: formId,
      submission_timestamp: data.submitted_at,
      user_data: {
        email_hash: data.hashed_email,
        phone_hash: data.hashed_phone,
      },
    };

    this.gtmService.pushToDataLayer(payload);
    console.log('Pushed Typeform data to GTM:', payload);
  }

  /**
   * Push to Meta Pixel (Facebook)
   */
  private pushToMetaPixel(data: TypeformHashedData): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Check if Facebook Pixel is loaded
    if (typeof (window as any).fbq !== 'undefined') {
      const advancedMatching: any = {};

      if (data.hashed_email) {
        advancedMatching.em = data.hashed_email;
      }

      if (data.hashed_phone) {
        advancedMatching.ph = data.hashed_phone;
      }

      // Track with advanced matching in the correct format
      // fbq('track', 'Lead', {}, { 'em': '<hash>', 'ph': '<hash>' })
      (window as any).fbq('track', 'Lead', advancedMatching);

      console.log('Pushed to Meta Pixel with hashed data:', {
        event: 'Lead',
        advancedMatching: advancedMatching,
      });
    }
  }

  /**
   * Push to TikTok Pixel
   */
  private pushToTikTokPixel(data: TypeformHashedData): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Check if TikTok Pixel is loaded
    if (typeof (window as any).ttq !== 'undefined') {
      const params: any = {
        content_name: 'Typeform Submission',
        content_category: 'Lead Generation',
      };

      // TikTok advanced matching
      const advancedMatching: any = {};

      if (data.hashed_email) {
        advancedMatching.sha256_email = data.hashed_email;
      }

      if (data.hashed_phone) {
        advancedMatching.sha256_phone_number = data.hashed_phone;
      }

      (window as any).ttq.track('SubmitForm', params, {
        event_id: `typeform_${Date.now()}`,
        user: advancedMatching,
      });

      console.log('Pushed to TikTok Pixel with hashed data');
    }
  }

  /**
   * Get or create a session ID for additional security
   */
  private getSessionId(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    let sessionId = sessionStorage.getItem('typeform_session_id');

    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem('typeform_session_id', sessionId);
    }

    return sessionId;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
